import { Suspense } from 'react';

import { Outlet } from 'react-router-dom';

import { TrialPaywallExperiment, TrialPricingModal } from '@/widgets/pricing/pricingCards';
import { TeamspaceSettingsModal } from '@/widgets/teamspaceSettings';

import { AcquisitionSurveyModal } from '@/features/acquisitionSurvey';
import { BookACallModal } from '@/features/bookACall';
import { BookSalesDemoModal } from '@/features/bookSalesDemo';
import { IntroduceBoostersModal, ManageBoostersModal, useBoostersPromo } from '@/features/boosters';
import { DebugMenu, useDebugMenuHotkey } from '@/features/debugMenu';
import { UpsellModalHost } from '@/features/pricing';
import { BuyMinutesModal } from '@/features/pricing/buyMinutes';
import { CancelPauseModal, useCancelPauseModalStore } from '@/features/pricing/cancelPause';
import { DowngradeConfirmationModal } from '@/features/pricing/downgradeConfirmation';
import { PlanFeaturesModal } from '@/features/pricing/paidTrial';
import { TrialPaywall } from '@/features/pricing/trialPaywall/ui';
import { UpgradeConfirmationModal } from '@/features/pricing/upgradeConfirmation';
import { OnscreenPaywallModal } from '@/features/project/visualTranslation';
import { RecordingExtensionPromptModal } from '@/features/recording/recordingExtensionPrompt';
import { RecordingIntroModal, useRecordingIntroGate } from '@/features/recording/recordingIntro';
import { RecordingShareModal } from '@/features/recordingShare';
import { TeamspaceWelcomeModal, useIsSpaceOwner } from '@/features/teamspace';

import { AfterPurchaseModal } from '@/entities/afterPurchase';
import { BOOK_SALES_DEMO_SOURCES, useOpenBookSalesDemo } from '@/entities/demoBooking';
import { usePendingPaymentSessionLifecycle } from '@/entities/paymentSession';
import { EnterprisePaymentRequiredModal, UpgradePlanModal } from '@/entities/pricing';
import { PurchaseConfirmationModal } from '@/entities/purchaseConfirmation/ui/PurchaseConfirmationModal';

import { useHandlePaymentSuccess } from './hooks/useHandlePaymentSuccess';

const BaseLayoutContent = () => {
  const isSpaceOwner = useIsSpaceOwner();
  const openCancelPauseModal = useCancelPauseModalStore((state) => state.open);
  const openBookSalesDemo = useOpenBookSalesDemo();

  useHandlePaymentSuccess();
  useBoostersPromo();
  useDebugMenuHotkey();
  usePendingPaymentSessionLifecycle();
  // Loads the once-per-user intro flag ahead of the first "new recording", which does not wait for it.
  useRecordingIntroGate();

  return (
    <>
      <Outlet />
      <UpgradePlanModal
        isSpaceOwner={isSpaceOwner}
        onContactSales={(sourceText) =>
          openBookSalesDemo(BOOK_SALES_DEMO_SOURCES.upgradePlanModalContactUs, sourceText)
        }
      />
      <UpgradeConfirmationModal />
      <DowngradeConfirmationModal />
      <EnterprisePaymentRequiredModal />
      <IntroduceBoostersModal />
      <PurchaseConfirmationModal />
      <AfterPurchaseModal />
      <ManageBoostersModal onOpenCancelPause={openCancelPauseModal} />
      <BuyMinutesModal />
      <TeamspaceSettingsModal />
      <TeamspaceWelcomeModal />
      <TrialPaywall />
      <TrialPricingModal />
      <TrialPaywallExperiment />
      <BookSalesDemoModal />
      <CancelPauseModal />
      <RecordingShareModal />
      <RecordingExtensionPromptModal />
      <RecordingIntroModal />
      <BookACallModal />
      <UpsellModalHost />
      <PlanFeaturesModal />
      <OnscreenPaywallModal />
      <AcquisitionSurveyModal />
      <DebugMenu />
    </>
  );
};

export const BaseLayout = () => (
  <Suspense>
    <BaseLayoutContent />
  </Suspense>
);
